import { useState } from 'react'
import { Navigate, useNavigate, useParams } from 'react-router-dom'

import { Box, Button, Text } from '@factorialco/deprecated-design-system'
import {
  SurveysTrainingClassFragment,
  TrainingSurveySendSurveyDocument,
  TrainingSurveysTrainingClassesDocument,
} from 'generated/resources/graphql'

import Callout from 'modules/core/components/Callout'
import Modal from 'modules/core/components/Modal'
import SelectField from 'modules/core/components/SelectField'
import Selector from 'modules/core/components/Selector'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useQuery } from 'modules/core/hooks/useQuery'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { EmployeeSelectorV2 } from 'modules/employees/components/EmployeeSelectorV2'
import Loading from 'modules/layout/components/Page/Loading'

interface SendSurveyModalProps {
  trainingId?: string
  formId?: string
  onClose?: () => void
}

const SendSurveyModal = ({
  trainingId: propTrainingId,
  formId: propFormId,
  onClose: propOnClose,
}: SendSurveyModalProps = {}) => {
  const navigate = useNavigate()
  const params = useParams<{ id: string; formId: string }>()

  // Use props if provided, otherwise fall back to URL params
  const id = propTrainingId ?? params.id
  const formId = propFormId ?? params.formId
  const isRouteMode = !propTrainingId && !propFormId

  const { navigation } = useNavigation()

  const [currentSelectedOptions, setCurrentSelectedOptions] = useState<number[]>([])
  const [selectedOption, setselectedOption] = useState<string>('manager')
  const [selectedEmployeeAccessId, setSelectedEmployeeAccessId] = useState<number | undefined>()

  const { data, loading: loadingTrainingGroups } = useQuery(
    TrainingSurveysTrainingClassesDocument,
    {
      variables: {
        formId: formId ?? '',
        trainingId: Number(id),
      },
    }
  )
  const [sendSurvey, { loading: sendingSurvey }] = useMutation(TrainingSurveySendSurveyDocument, {
    extractorFn: (mutations) => mutations.formsMutations.sendFormFormsForm,
  })
  const isEffecttivenessSurvey = data?.forms?.form?.formType === 'effectiveness'
  const isAnonymousSurvey = data?.forms?.form?.anonymous

  const disabled =
    currentSelectedOptions.length === 0 ||
    sendingSurvey ||
    (isEffecttivenessSurvey && selectedOption === 'manualselection' && !selectedEmployeeAccessId)
  const handledSubmit = async () => {
    if (!formId) {
      return Promise.reject('')
    }

    const configuration = isEffecttivenessSurvey
      ? (() => {
          const respondentIds = selectedEmployeeAccessId
            ? [selectedEmployeeAccessId.toString()]
            : []

          return {
            targetStrategy: selectedOption,
            respondentIds,
          }
        })()
      : {
          targetStrategy: 'self',
          respondentIds: [],
        }

    const response = await sendSurvey({
      variables: {
        id: formId,
        resourceIds: currentSelectedOptions.map((option) => option.toString()),
        respondentId: selectedEmployeeAccessId?.toString() ?? null,
        configuration,
      },
    })

    const handledResponse = handleResult(response, () => {
      app.setFlash({ type: 'wrong', message: i18n.t('trainings.surveys.create.send_survey.error') })
    })
    if (handledResponse.success) {
      app.setFlash({
        type: 'correct',
        message: i18n.t('trainings.surveys.create.send_survey.success'),
      })
      if (propOnClose) {
        propOnClose()
      } else {
        navigate(onCloseRoute)
      }
    }
  }
  const options = data?.trainings.trainingClassesConnection.nodes ?? []
  const emptyGroups = options.length === 0
  const isFormSentToAllGroups = (trainingClasses: SurveysTrainingClassFragment[]) => {
    return trainingClasses.every((trainingClass) =>
      trainingClass.materializedFormsConnection.nodes.some(
        (materializedForm) => materializedForm.form?.id === formId
      )
    )
  }
  const isSent = formId && isFormSentToAllGroups(options)
  const noGroupMessage = i18n.t('trainings.surveys.create.send_survey.no_group_message')
  const moreInfoMessage = i18n.t('trainings.surveys.create.send_survey.more_info_message')
  const managersOptions = [
    {
      value: 'manager',
      label: i18n.t('trainings.surveys.create.send_survey.manager'),
    },
    {
      value: 'manualselection',
      label: i18n.t('trainings.surveys.create.send_survey.manual_selection'),
    },
  ]

  const onCloseRoute =
    navigation.trainings.list.details_revamp.trainingSurveyDetail.questions.fullPath({
      id: Number(id),
      formId: Number(formId),
    })

  const description = isEffecttivenessSurvey
    ? i18n.t('trainings.surveys.create.send_survey.description_effectiveness')
    : i18n.t('trainings.surveys.create.send_survey.description_satisfaction')

  const getDescriptionMessage = () => {
    if (emptyGroups) return noGroupMessage
    if (isSent) return i18n.t('trainings.surveys.create.send_survey.sent_to_all_groups')
    if (isAnonymousSurvey)
      return i18n.t('trainings.surveys.create.send_survey.more_info_message_anonymous')
    return moreInfoMessage
  }

  if (isRouteMode && (!id || !formId)) return <Navigate to={onCloseRoute} />
  if (loadingTrainingGroups) return <></>
  return (
    <Modal
      title={i18n.t('trainings.surveys.create.send_survey.title')}
      description={description}
      illustration="document-edit"
      onClose={propOnClose ?? (() => navigate(onCloseRoute))}
      onSubmit={() => handledSubmit()}
      footer={
        <Box flexDirection="row" gap="s8">
          {propOnClose ? (
            <Button type="secondary" onClick={propOnClose}>
              {i18n.t('common.cancel')}
            </Button>
          ) : (
            <Button.Router to={onCloseRoute} type="secondary">
              {i18n.t('common.cancel')}
            </Button.Router>
          )}
          <Button submit disabled={disabled}>
            {i18n.t('common.send')}
          </Button>
        </Box>
      }
    >
      {sendingSurvey ? (
        <Loading />
      ) : (
        <Box gap="s24">
          <Box gap="s4">
            <Text.Normal weight="medium" size="200" color="grey1000">
              {i18n.t('trainings.surveys.create.send_survey.groups_title')}
            </Text.Normal>
            <Selector.Popover
              isDisabled={options.length === 0}
              hasSearch
              placeholder={i18n.t('common.select')}
              searchPlaceholder=""
              value={currentSelectedOptions}
              blankLabel=""
              onChange={(selection) => {
                if (Array.isArray(selection)) {
                  setCurrentSelectedOptions(selection)
                }
              }}
              isMultiSelect
            >
              {options.map((option) => {
                const materializedFormsIds = option.materializedFormsConnection.nodes.map(
                  (op) => op.form?.id
                )
                return (
                  <Selector.Option
                    value={option.id}
                    label={option.name}
                    key={option.id}
                    searchValue={option.name}
                    isDisabled={materializedFormsIds.includes(formId)}
                  >
                    {option.name}
                  </Selector.Option>
                )
              })}
            </Selector.Popover>
          </Box>
          {isEffecttivenessSurvey ? (
            <Box gap="s16">
              <Text.Normal weight="medium" size="200" color="grey1000">
                {i18n.t('trainings.surveys.create.send_survey.managers_selector_title')}
              </Text.Normal>
              <SelectField
                options={managersOptions}
                onChange={(selection) => setselectedOption(selection)}
              />
              {selectedOption === 'manualselection' && (
                <EmployeeSelectorV2
                  value={selectedEmployeeAccessId}
                  onChange={setSelectedEmployeeAccessId}
                  useAccessId
                />
              )}
            </Box>
          ) : (
            <Callout
              title={
                emptyGroups
                  ? i18n.t('trainings.surveys.create.send_survey.empty_groups')
                  : undefined
              }
              type={emptyGroups || isSent ? 'warning' : 'info'}
              description={getDescriptionMessage()}
              hideIcon
              action={
                emptyGroups ? (
                  <Button.Router
                    title={i18n.t('trainings.surveys.create.send_survey.create_group')}
                    to={navigation.trainings.list.details_revamp.view.trainingClasses.tab.newClass.fullPath(
                      {
                        id: Number(id),
                      }
                    )}
                    type="secondary"
                  >
                    {i18n.t('trainings.surveys.create.send_survey.create_group')}
                  </Button.Router>
                ) : isSent ? (
                  <Button.Router
                    title={i18n.t('trainings.surveys.create.send_survey.create_group')}
                    to={navigation.trainings.list.details_revamp.view.trainingClasses.tab.newClass.fullPath(
                      {
                        id: Number(id),
                      }
                    )}
                    type="secondary"
                  >
                    {i18n.t('trainings.surveys.create.send_survey.create_group')}
                  </Button.Router>
                ) : (
                  <></>
                )
              }
            />
          )}
        </Box>
      )}
    </Modal>
  )
}

export default SendSurveyModal
